/**
 * Digitize tool.
 *
 * Takes an image — either a raster (image/GeoTIFF) layer already on the map,
 * or a PNG/JPG uploaded straight into this panel — sends it plus its
 * real-world bounds to the backend's CV pipeline (`backend/digitize_router.py`),
 * and drops the traced lines and polygons in as a new temporary layer — the
 * same destination Buffer/Clip's own results land in.
 *
 * An uploaded PNG/JPG carries no georeferencing of its own (unlike a GeoTIFF
 * or an already-placed raster layer), so it is placed at the current map
 * view — see `geotiff.ts`'s `parsePlainImageFile`, the same placement
 * "Add data" gives a plain image added as a layer.
 *
 * This is for a CLEAN, high-contrast scanned drawing (an engineering or site
 * plan, a cadastral sheet — ink on a light background), traced by threshold
 * + contour + skeleton, not a trained model: it has no idea what a road or a
 * building IS, only where the dark pixels are. Said plainly in the panel
 * itself, not just here, since a photo or a low-contrast scan will produce a
 * result that looks like noise, not a wrong answer explained anywhere.
 */

import { useRef, useState } from 'react';
import { Loader2, Upload, Wand2 } from 'lucide-react';
import ToolPanelHeader, { ToolActions } from '../ToolPanel';
import { scopedLayers, type ToolContext } from './types';
import { EmptyHint, Field, LayerSelect, Notice, btnClass } from './shared';
import { getLayerDisplayName } from '../../../layers/capabilities';
import { parsePlainImageFile, type ParsedPlainImage } from '../../../layers/geotiff';
import type { LayerRenderer } from '../../../types';
import { digitizeService, type DigitizeSimplification } from '../../../services/digitizeService';

type SourceMode = 'layer' | 'upload';

export function DigitizePanel({ ctx }: { ctx: ToolContext }) {
  const allowed = scopedLayers(ctx.config, ctx.layers);
  // Beyond what `scopedLayers` can express: only a raster (image) layer has
  // anything for this tool to trace. The same "narrow the admin's own layer
  // list by what the tool can actually use" pattern Clip applies for
  // geometryType.
  const candidates = allowed.filter((layer) => Boolean(layer.localRaster));

  const [sourceMode, setSourceMode] = useState<SourceMode>(candidates.length > 0 ? 'layer' : 'upload');
  const [layerId, setLayerId] = useState(candidates[0]?.id ?? '');
  const [upload, setUpload] = useState<(ParsedPlainImage & { fileName: string }) | null>(null);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);

  const [simplification, setSimplification] = useState<DigitizeSimplification>('medium');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<{ lines: number; polygons: number } | null>(null);

  const source = candidates.find((layer) => layer.id === layerId);

  const handleUpload = async (file: File | undefined) => {
    if (!file) return;
    setUploadError(null);
    setUpload(null);
    setUploadBusy(true);
    try {
      const parsed = await parsePlainImageFile(file, ctx.mapCenter, ctx.mapZoom);
      setUpload({ ...parsed, fileName: file.name });
    } catch (failure) {
      setUploadError(failure instanceof Error ? failure.message : 'This image could not be read.');
    } finally {
      setUploadBusy(false);
    }
  };

  const run = async () => {
    const image =
      sourceMode === 'layer'
        ? source?.localRaster && source.bounds
          ? { base64: source.localRaster.base64, mimeType: source.localRaster.mimeType, bounds: source.bounds, name: getLayerDisplayName(source) }
          : null
        : upload
          ? { base64: upload.base64, mimeType: upload.mimeType, bounds: upload.bounds, name: upload.fileName }
          : null;

    if (!image) {
      if (sourceMode === 'layer' && source && !source.bounds) {
        setError('This layer has no real-world extent recorded, so a traced shape would have nowhere to go.');
      }
      return;
    }

    setError(null);
    setSummary(null);
    setBusy(true);
    try {
      const result = await digitizeService.run(
        image.base64,
        image.mimeType || 'image/png',
        image.bounds,
        simplification,
      );

      if (result.features.length === 0) {
        setError('Nothing traceable was found — the image may be too low-contrast, or already too simple to trace.');
        return;
      }

      // Two layers, not one tagged by a `shape` field — a line and a polygon
      // are different enough (different attribute tables, different tools
      // make sense on each) that lumping them into one layer just to share
      // a renderer is the wrong trade, the same reason Buffer/Clip never do
      // it either.
      const lineFeatures = result.features.filter((feature) => feature.properties?.shape === 'line');
      const polygonFeatures = result.features.filter((feature) => feature.properties?.shape === 'polygon');

      if (lineFeatures.length > 0) {
        const lineRenderer: LayerRenderer = {
          type: 'single',
          color: '#DC2626',
          fillOpacity: 0.3,
          outlineColor: '#991B1B',
          outlineWidth: 1.5,
          pointSize: 8,
        };
        ctx.onCreateTempLayer?.(
          `Digitized lines — ${image.name}`,
          { type: 'FeatureCollection', features: lineFeatures as unknown as GeoJSON.Feature[] },
          'Digitize',
          lineRenderer,
        );
      }

      if (polygonFeatures.length > 0) {
        const polygonRenderer: LayerRenderer = {
          type: 'single',
          color: '#2563EB',
          fillOpacity: 0.3,
          outlineColor: '#1D4ED8',
          outlineWidth: 1.5,
          pointSize: 8,
        };
        ctx.onCreateTempLayer?.(
          `Digitized polygons — ${image.name}`,
          { type: 'FeatureCollection', features: polygonFeatures as unknown as GeoJSON.Feature[] },
          'Digitize',
          polygonRenderer,
        );
      }

      setSummary({ lines: result.lineCount, polygons: result.polygonCount });
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : 'Could not digitize that image.');
    } finally {
      setBusy(false);
    }
  };

  const canRun = sourceMode === 'layer' ? Boolean(source) : Boolean(upload);

  return (
    <>
      <ToolPanelHeader
        title="Digitize"
        description="Trace a scanned drawing automatically into line and polygon features."
        onClose={ctx.onClose}
      />

      {/* A layer already on the map carries its own real-world bounds; an
          upload here has none, and is placed at the current map view
          instead (see `parsePlainImageFile`) — different enough underneath
          that the choice is explicit rather than inferred from what's
          picked. */}
      <div className="flex gap-1 rounded-md bg-slate-100 p-0.5 text-[11px] font-medium">
        {([
          { id: 'layer' as const, label: 'Layer on map' },
          { id: 'upload' as const, label: 'Upload image' },
        ]).map((entry) => (
          <button
            key={entry.id}
            type="button"
            onClick={() => setSourceMode(entry.id)}
            className={`flex-1 rounded px-2 py-1 transition ${
              sourceMode === entry.id ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            {entry.label}
          </button>
        ))}
      </div>

      {sourceMode === 'layer' ? (
        <>
          <Field
            label="Image to digitize"
            hint="A raster layer already on the map — import one from Add Data first if none is listed."
          >
            <LayerSelect layers={candidates} value={layerId} onChange={setLayerId} />
          </Field>
          {candidates.length === 0 && (
            <EmptyHint>No image/GeoTIFF layers are on the map yet. Add one from Add Data first, or switch to "Upload image" above.</EmptyHint>
          )}
        </>
      ) : (
        <Field label="Image to digitize" hint="A PNG or JPG from your computer — not yet on the map.">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={uploadBusy}
              className={`${btnClass} gl-btn--secondary`}
            >
              {uploadBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
              {uploadBusy ? 'Reading…' : 'Choose image'}
            </button>
            {upload && !uploadBusy && (
              <span className="min-w-0 flex-1 truncate text-[11px] text-slate-500">{upload.fileName}</span>
            )}
          </div>
          <input
            ref={fileRef}
            type="file"
            accept=".png,.jpg,.jpeg"
            className="hidden"
            onChange={(event) => {
              void handleUpload(event.target.files?.[0]);
              event.target.value = '';
            }}
          />
          {uploadError && <p className="mt-1.5 text-[11px] text-red-600">{uploadError}</p>}
          {upload && !uploadError && (
            <p className="mt-1.5 text-[10.5px] leading-snug text-slate-400">
              {upload.width.toLocaleString()}×{upload.height.toLocaleString()} px.{' '}
              {upload.notices.join(' ')}
            </p>
          )}
        </Field>
      )}

      {/* Low keeps the most detail (small simplification tolerance); High
          smooths the result down to the fewest points. Three buttons, not a
          raw pixel-tolerance field — the same "name the outcome, not the
          number" choice Buffer's own ring-mode toggle already makes. */}
      <div className="flex gap-1 rounded-md bg-slate-100 p-0.5 text-[11px] font-medium">
        {(['low', 'medium', 'high'] as const).map((level) => (
          <button
            key={level}
            type="button"
            onClick={() => setSimplification(level)}
            className={`flex-1 rounded px-2 py-1 capitalize transition ${
              simplification === level ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            {level}
          </button>
        ))}
      </div>
      <p className="text-[10.5px] leading-snug text-slate-400">
        How much to simplify the traced shapes — Low keeps the most detail, High smooths them to the
        fewest points.
      </p>

      <ToolActions>
        <button
          type="button"
          onClick={() => void run()}
          disabled={!canRun || busy}
          className={`${btnClass} gl-btn--primary`}
        >
          {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
          {!busy && <Wand2 className="h-3.5 w-3.5" />}
          {busy ? 'Tracing…' : 'Digitize'}
        </button>
      </ToolActions>

      {error && <Notice tone="warn">{error}</Notice>}

      {summary && (
        <Notice>
          Traced {summary.lines} line{summary.lines === 1 ? '' : 's'} and {summary.polygons} polygon
          {summary.polygons === 1 ? '' : 's'} — added to the Layers tab as{' '}
          {summary.lines > 0 && summary.polygons > 0 ? 'two temporary layers' : 'a temporary layer'}, one
          for each shape found. They stay until you remove them, and nobody else sees them.
        </Notice>
      )}

      <p className="text-[10.5px] leading-snug text-slate-400">
        Works best on a clean, high-contrast scanned drawing — ink on a light background. This follows
        pixel patterns, not a trained model: it has no idea what a road or a building IS, only where
        the dark pixels are, so a photo or a low-contrast scan will trace poorly.
      </p>
    </>
  );
}
